import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  Grid,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

import {
  CargoCompany,
  CreateCargoCompanyRequest,
  UpdateCargoCompanyRequest,
} from "../../../types/cargoCompany";
import {
  validateCreateCargoCompany,
  validateUpdateCargoCompany,
  getFieldErrors,
  CreateCargoCompanyFormData,
  UpdateCargoCompanyFormData,
} from "../../../validation/cargoCompanySchema";
import { formatApiError } from "../../../utils/formatApiError";

interface CargoCompanyFormModalProps {
  open: boolean;
  mode: "create" | "edit";
  company?: CargoCompany;
  onSubmit: (data: CreateCargoCompanyRequest | UpdateCargoCompanyRequest) => void;
  onClose: () => void;
  isLoading?: boolean;
  error?: any;
}

const CargoCompanyFormModal: React.FC<CargoCompanyFormModalProps> = ({
  open,
  mode,
  company,
  onSubmit,
  onClose,
  isLoading = false,
  error,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<CreateCargoCompanyFormData>({
    name: "",
    code: "",
    contactEmail: "",
    contactPhone: "",
    address: "",
    isActive: true,
    settings: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string>("");

  const isEditMode = mode === "edit";
  const isCreateMode = mode === "create";

  useEffect(() => {
    if (open) {
      if (isEditMode && company) {
        setFormData({
          name: company.name || "",
          code: company.code || "",
          contactEmail: company.contactEmail || "",
          contactPhone: company.contactPhone || "",
          address: company.address || "",
          isActive: company.isActive ?? true,
          settings: company.settings || "",
        });
      } else {
        setFormData({
          name: "",
          code: "",
          contactEmail: "",
          contactPhone: "",
          address: "",
          isActive: true,
          settings: "",
        });
      }
      setErrors({});
      setSubmitError("");
    }
  }, [open, mode, company, isEditMode]);

  useEffect(() => {
    if (error) {
      const errorMessage = formatApiError(error);
      setSubmitError(errorMessage);
    }
  }, [error]);

  const handleInputChange = (
    field: keyof CreateCargoCompanyFormData,
    value: any,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
    if (submitError) {
      setSubmitError("");
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    let validationResult;
    if (isCreateMode) {
      validationResult = validateCreateCargoCompany(formData);
    } else {
      // For update, only include fields that have values
      const updateData: UpdateCargoCompanyFormData = {};
      if (formData.name) updateData.name = formData.name;
      if (formData.code) updateData.code = formData.code;
      if (formData.contactEmail) updateData.contactEmail = formData.contactEmail;
      if (formData.contactPhone !== undefined)
        updateData.contactPhone = formData.contactPhone;
      if (formData.address !== undefined) updateData.address = formData.address;
      if (formData.isActive !== undefined)
        updateData.isActive = formData.isActive;
      if (formData.settings !== undefined) updateData.settings = formData.settings;

      validationResult = validateUpdateCargoCompany(updateData);
    }

    if (!validationResult.success) {
      setErrors(getFieldErrors(validationResult));
      return;
    }

    setErrors({});
    setSubmitError("");

    if (isCreateMode) {
      onSubmit(validationResult.data as CreateCargoCompanyRequest);
    } else {
      // For update, only send changed fields
      const updateData: UpdateCargoCompanyRequest = {};
      if (formData.name !== company?.name) updateData.name = formData.name;
      if (formData.code !== company?.code) updateData.code = formData.code;
      if (formData.contactEmail !== company?.contactEmail)
        updateData.contactEmail = formData.contactEmail;
      if (formData.contactPhone !== company?.contactPhone)
        updateData.contactPhone = formData.contactPhone || undefined;
      if (formData.address !== company?.address)
        updateData.address = formData.address || undefined;
      if (formData.isActive !== company?.isActive)
        updateData.isActive = formData.isActive;
      if (formData.settings !== company?.settings)
        updateData.settings = formData.settings || undefined;

      onSubmit(updateData);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {isCreateMode ? t("admin.createCargoCompany") : t("admin.editCargoCompany")}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("admin.companyName")}
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                required
                disabled={isLoading}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("common.code")}
                value={formData.code}
                onChange={(e) => handleInputChange("code", e.target.value)}
                error={!!errors.code}
                helperText={errors.code || t("admin.codeHelperText")}
                required
                disabled={isLoading}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("admin.contactEmail")}
                type="email"
                value={formData.contactEmail}
                onChange={(e) => handleInputChange("contactEmail", e.target.value)}
                error={!!errors.contactEmail}
                helperText={errors.contactEmail}
                required
                disabled={isLoading}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("admin.contactPhone")}
                value={formData.contactPhone || ""}
                onChange={(e) => handleInputChange("contactPhone", e.target.value)}
                error={!!errors.contactPhone}
                helperText={errors.contactPhone}
                disabled={isLoading}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("common.address")}
                value={formData.address || ""}
                onChange={(e) => handleInputChange("address", e.target.value)}
                error={!!errors.address}
                helperText={errors.address}
                multiline
                rows={3}
                disabled={isLoading}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("admin.settingsJson")}
                value={formData.settings || ""}
                onChange={(e) => handleInputChange("settings", e.target.value)}
                error={!!errors.settings}
                helperText={errors.settings || t("admin.settingsJsonHelperText")}
                multiline
                rows={3}
                disabled={isLoading}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => handleInputChange("isActive", e.target.checked)}
                    disabled={isLoading}
                  />
                }
                label={t("common.active")}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={isLoading}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading
              ? isCreateMode
                ? t("admin.creating")
                : t("admin.updating")
              : isCreateMode
              ? t("common.create")
              : t("common.update")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CargoCompanyFormModal;

